import {
  Component, computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {standardsGroup, teamMember} from "../team-management.component";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {
  MemberAssignmentToStandardsComponent
} from "../member-assignment-to-standards/member-assignment-to-standards.component";
import {ApiCallService} from "../../../../../services/api-call.service";
import {first} from "rxjs/operators";
import {ErrorMessageService} from "../../../../../services/error-message.service";
import {Session} from "../../../../../services/session.service";
import {MatIcon} from "@angular/material/icon";

@Component({
  selector: 'app-edit-team-member',
  imports: [
    MatSlideToggle,
    MemberAssignmentToStandardsComponent,
    MatIcon
  ],
  templateUrl: './edit-team-member.component.html',
  styleUrl: './edit-team-member.component.scss'
})
export class EditTeamMemberComponent implements OnInit{
  private readonly api = inject(ApiCallService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly session = inject(Session);

  member = model.required<teamMember>();
  groups = input.required<standardsGroup[]>();
  hasFullAccess: WritableSignal<boolean> = signal(false);
  isCurrentUser = computed(() => { return this.session.getUser().userRequestKey === this.member().requestKey })
  // Clone groups to avoid mutating the original array
  memberGroups: WritableSignal<standardsGroup[]> = signal([]);
  loaded: WritableSignal<boolean> = signal(false);
  error: WritableSignal<string> = signal("");
  private email: string;

  constructor() {
    effect(() => {
      if (this.groups() && this.groups().length) {
        // Deep clone to avoid mutating parent input
        const cloned = this.groups().map(g => ({
          ...g,
          standards: g.standards.map(s => ({ ...s }))
        }));
        this.memberGroups.set(cloned);
      }
    });
  }

  ngOnInit() {
    this.getAccountUser();
  }

  toggleMemberRole(){
    this.member.update( member => {
      member.userRole = member.userRole === 1 ? 2 : 1;
      return {...member};
    });
    if(!this.hasFullAccess()){
      this.toggleFullAccess();
      return;
    }
    this.saveAccountUser();
  }

  toggleFullAccess(){
    this.hasFullAccess.update( val => { return !val });
    if(this.hasFullAccess()) {
      this.memberGroups.update( groups => {
        groups.forEach(group => {
          group.checked = true;
          group.standards.forEach(standard => standard.checked = true);
        });
        return [...groups];
      });
      this.saveAccountUser();
    }
  }

  removeMember(){
    this.hasFullAccess.set(false);
    this.memberGroups.update( groups => {
      groups.forEach(group => {
        group.checked = false;
        group.standards.forEach(standard => standard.checked = false);
      });
      return [...groups];
    });
    this.saveAccountUser();
  }

  getAccountUser() {
    this.api.postJsonData('/getAccountUser/', { requestKey: this.member().requestKey })
      .pipe(first())
      .subscribe(response => {
        this.loaded.set(true);
        if (response && response.success) {
          const data = response.data[0].dataSet;
          const standardsAssigned: string[] = data.standardAccess || [];
          this.email = data.email;
          this.memberGroups.update(groups => {
            for (const group of this.memberGroups()) {
              for (const standard of group.standards) {
               standard.checked = standardsAssigned.includes(standard.requestKey) || this.member().userRole === 2;
              }
              // Update group checked status based on its standards
              group.checked = group.standards.every(t => t.checked);
            }
            return [...groups];
          });
          this.hasFullAccess.update( val => {
            return this.memberGroups().every(group => group.checked);
          });
        }
        else{
          if(response && Array.isArray(response.dataError) && response.dataError.length > 0) {
            this.error.set("There was a problem with loading the member's information. The server responded with: " + response.dataError[0].message);
          }
          else{
            this.error.set("There was a problem with loading the member's information. The server did not return any error messages. Please contact the administrator.");
          }
        }
    });
  }

  onGroupsChange(groups: standardsGroup[]) {
    this.memberGroups.set(groups);
    this.saveAccountUser();
  }

  saveAccountUser() {
    const standardsAssigned: string[] = [];
    for (const group of this.memberGroups()) {
      for (const standard of group.standards) {
        if (standard.checked) {
          standardsAssigned.push(standard.requestKey);
        }
      }
    }
    this.api.postJsonData('/saveAccountUser/', {
      requestKey: this.member().requestKey,
      name: this.member().name,
      email: this.email,
      accountRoleId: this.member().userRole,
      standardAccess: standardsAssigned,
    })
    .pipe(first())
    .subscribe(response => {
      if(response && response.success){

      }
      else{
        if(response && Array.isArray(response.dataError) && response.dataError.length > 0) {
          this.errorMessageService.showErrorModal("There was a problem with saving the member's information. The server responded with: " + response.dataError[0].message, response.dataError);
        }
        else{
          this.errorMessageService.showErrorModal("There was a problem with saving the member's information. The server did not return any error messages. Please contact the administrator.", []);
        }
      }
    });
  }
}
